import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  MenuItem,
  Menu,
  IconButton,
} from "@material-ui/core";
import { GetPropertySpecificationByPropertyId } from "../../../../../../Services";
import { CustomCard, CustomBadge } from "../../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../../Hooks";
import { useVerticalNav } from "../../../../../../Contexts/VerticalNavContext";

// icons
import {
  BuildingIcon,
  ArrowNarrowRight,
  UnitSizeIcon,
  BedIcon,
  EditIcon,
  Building03,
  MoreVertIcon,
  HouseModern,
  Building08,
  Building09,
  Building02,
  Briefcase01,
  Warehouse,
} from "../../../../../../assets/icons";

// styles
import useStyles from "./styles";

function Specifications({ propretyId }) {
  const styles = useStyles();
  const { translate } = useTranslate("NewPropertiesView");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { setAlertBoxContent } = useVerticalNav();

  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [specifications, setSpecifications] = useState(null);

  const options = [
    {
      label: "Edit Contact",
      icon: (
        <EditIcon width="16" height="16" fill={palette.foreground.quarterary} />
      ),
      handleOnclick: () => {
        // handle edit logic here
      },
    },
  ];

  const getSpecifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await GetPropertySpecificationByPropertyId(propretyId);
      if (!(res && res.status && res.status !== 200)) {
        setSpecifications(res);
      } else {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("Something-went-wrong"),
          onClose: () => setAlertBoxContent(null),
        });
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("Something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  });

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl({
      top: rect.top + 25,
      left: rect.left + 15,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    getSpecifications();
  }, [propretyId]);

  const renderPropertyCard = ({
    icon,
    title,
    badgeLabel,
    minArea,
    maxArea,
    unitTypesLeft,
    unitTypesRight,
    showStudios,
    studiosCount,
  }) => (
    <CustomCard borderRadius="2xl" classes={styles.card}>
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {icon}
            <Typography className={styles.title}>{title}</Typography>
            <CustomBadge
              Style={{ padding: "4px 12px" }}
              label={badgeLabel}
              SizeVariant={"large"}
              BackgroundColor={palette.utility.brand_50}
              BorderColor={palette.utility.brand_200}
              Color={palette.utility.brand_700}
            />
          </Box>
          <Box display="flex" alignItems="center" mt={1}>
            <UnitSizeIcon
              width="20"
              height="20"
              fill={palette.foreground.quaternary}
              style={{ marginRight: 4 }}
            />
            <Typography className={styles.secondary}>
              {minArea} - {maxArea}{" "}
              <span style={{ fontWeight: 600, fontSize: "16px" }}>sq.ft</span>
            </Typography>
          </Box>
        </Box>
        <Box>
          <IconButton
            aria-controls="more-button"
            aria-haspopup="true"
            aria-label="more-button"
            id="more-button"
            disableRipple
            onClick={handleClick}
            className={styles.menuIcon}
          >
            <MoreVertIcon
              width="20"
              height="20"
              fill={
                anchorEl
                  ? palette.foreground.quarterary
                  : palette.foreground.quinary
              }
            />
          </IconButton>
          <Menu
            id="more-button"
            PaperProps={{ elevation: 0 }}
            MenuListProps={{ className: styles.customMenuList }}
            anchorReference="anchorPosition"
            anchorPosition={
              anchorEl ? { top: anchorEl.top, left: anchorEl.left } : undefined
            }
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {options.map(({ label, icon, handleOnclick }) => (
              <MenuItem
                disableRipple
                key={label}
                onClick={(event) => {
                  event.stopPropagation();
                  handleOnclick();
                  handleClose();
                }}
                className={styles.option}
              >
                {icon}
                <span>{label}</span>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Divider />
      <Box className={styles.content} style={{ width: "100%" }}>
        <Box style={{ flex: 1 }}>
          {unitTypesLeft.map(({ label, value }, idx) => (
            <Box display="flex" alignItems="center" mb={1.5} key={label + idx}>
              {label.startsWith("x") && (
                <BedIcon
                  width={24}
                  height={24}
                  fill={palette.foreground.quinary}
                  style={{ marginRight: 6 }}
                />
              )}
              <Typography className={styles.unitTypes} style={{ minWidth: 36 }}>
                {label}
              </Typography>
              <ArrowNarrowRight
                width={24}
                height={24}
                fill={palette.foreground.primary_fg}
                style={{ margin: "0 8px" }}
              />
              <Typography className={styles.secondary}>{value}</Typography>
            </Box>
          ))}
          {showStudios && (
            <Box display="flex" alignItems="center" mb={1.5}>
              <Typography className={styles.unitTypes} style={{ minWidth: 36 }}>
                {translate("studios")}
              </Typography>
              <ArrowNarrowRight
                width={24}
                height={24}
                fill={palette.foreground.primary_fg}
                style={{ margin: "0 8px" }}
              />
              <Typography className={styles.secondary}>
                {studiosCount}
              </Typography>
            </Box>
          )}
        </Box>
        <Box style={{ flex: 1 }}>
          {unitTypesRight.map(({ label, value }, idx) => (
            <Box display="flex" alignItems="center" mb={1.5} key={label + idx}>
              {label.startsWith("x") || label === "6+" ? (
                <BedIcon
                  width={24}
                  height={24}
                  fill={palette.foreground.quinary}
                  style={{ marginRight: 6 }}
                />
              ) : null}
              <Typography className={styles.unitTypes} style={{ minWidth: 36 }}>
                {label}
              </Typography>
              <ArrowNarrowRight
                width={24}
                height={24}
                fill={palette.foreground.primary_fg}
                style={{ margin: "0 8px" }}
              />
              <Typography className={styles.secondary}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </CustomCard>
  );

  const SimplePropertyCard = ({
    icon,
    title,
    badgeLabel,
    mainValue,
    subValue,
  }) => (
    <CustomCard borderRadius="2xl" classes={styles.card}>
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {icon}
            <Typography className={styles.title}>{title}</Typography>
            {badgeLabel && (
              <CustomBadge
                Style={{ padding: "4px 12px" }}
                label={badgeLabel}
                SizeVariant={"large"}
                BackgroundColor={palette.utility.brand_50}
                BorderColor={palette.utility.brand_200}
                Color={palette.utility.brand_700}
              />
            )}
          </Box>
        </Box>
        <Box>
          <IconButton
            aria-controls="more-button"
            aria-haspopup="true"
            aria-label="more-button"
            id="more-button"
            disableRipple
            onClick={handleClick}
            className={styles.menuIcon}
          >
            {options?.icon}
          </IconButton>
          <Menu
            id="more-button"
            PaperProps={{ elevation: 0 }}
            MenuListProps={{ className: styles.customMenuList }}
            anchorReference="anchorPosition"
            anchorPosition={
              anchorEl ? { top: anchorEl.top, left: anchorEl.left } : undefined
            }
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {options?.items?.map(({ label, icon, handleOnclick }) => (
              <MenuItem
                disableRipple
                key={label}
                onClick={(event) => {
                  event.stopPropagation();
                  handleOnclick();
                  handleClose();
                }}
                className={styles.option}
              >
                {icon}
                <span>{label}</span>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Divider />
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        minHeight={180}
        width="100%"
      >
        <Typography className={styles.mainHeader}>{mainValue}</Typography>
        <Typography className={styles.subHeader}>{subValue}</Typography>
      </Box>
    </CustomCard>
  );

  return (
    <Box className={styles.container}>
      {/* Apartments */}
      {renderPropertyCard({
        icon: (
          <Building03
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("apartments"),
        badgeLabel: `No. ${specifications?.numberOfApartment}`,
        minArea: specifications?.minApartmentArea,
        maxArea: specifications?.maxApartmentArea,
        unitTypesLeft: [
          {
            label: "x1",
            value: specifications?.numberOfApartmentOneBedroom || 0,
          },
          {
            label: "x3",
            value: specifications?.numberOfApartmentThreeBedroom || 0,
          },
          {
            label: "x5",
            value: specifications?.numberOfApartmentFiveBedroom || 0,
          },
        ],
        unitTypesRight: [
          {
            label: "x2",
            value: specifications?.numberOfApartmentTowBedroom || 0,
          },
          {
            label: "x4",
            value: specifications?.numberOfApartmentFourBedroom || 0,
          },
          {
            label: "6+",
            value: specifications?.numberOfApartmentBedroomMoreThanFive || 0,
          },
        ],
        showStudios: true,
        studiosCount: specifications?.numberOfApartmentStudio || 0,
      })}
      {/* Villa */}
      {renderPropertyCard({
        icon: (
          <HouseModern
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("villa"),
        badgeLabel: `No. ${specifications?.numberOfVilla}`,
        minArea: specifications?.minVillaArea,
        maxArea: specifications?.maxVillaArea,
        unitTypesLeft: [
          {
            label: "x1",
            value: specifications?.numberOfVillaOneBedroom || 0,
          },
          {
            label: "x3",
            value: specifications?.numberOfVillaThreeBedroom || 0,
          },
          {
            label: "x5",
            value: specifications?.numberOfVillaFiveBedroom || 0,
          },
        ],
        unitTypesRight: [
          {
            label: "x2",
            value: specifications?.numberOfVillaTowBedroom || 0,
          },
          {
            label: "x4",
            value: specifications?.numberOfVillaFourBedroom || 0,
          },
          {
            label: "6+",
            value: specifications?.numberOfVillaBedroomMoreThanFive || 0,
          },
        ],
        showStudios: false,
        studiosCount: 0,
      })}
      {/* Studio */}
      {SimplePropertyCard({
        icon: (
          <BuildingIcon
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("studio"),
        badgeLabel: `No. ${specifications?.numberOfStudio}`,
        mainValue: `${specifications?.minStudioArea} - ${specifications?.maxStudioArea} sq.ft`,
        subValue: translate("min-max-size"),
      })}
      {/* Townhouse */}
      {renderPropertyCard({
        icon: (
          <Building09
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("townhouse"),
        badgeLabel: `No. ${specifications?.numberOfTownHouse}`,
        minArea: specifications?.minTownHouseArea,
        maxArea: specifications?.maxTownHouseArea,
        unitTypesLeft: [
          {
            label: "x1",
            value: specifications?.numberOfTownHouseOneBedroom || 0,
          },
          {
            label: "x3",
            value: specifications?.numberOfTownHouseThreeBedroom || 0,
          },
          {
            label: "x5",
            value: specifications?.numberOfTownHouseFiveBedroom || 0,
          },
        ],
        unitTypesRight: [
          {
            label: "x2",
            value: specifications?.numberOfTownHouseTowBedroom || 0,
          },
          {
            label: "x4",
            value: specifications?.numberOfTownHouseFourBedroom || 0,
          },
          {
            label: "6+",
            value: specifications?.numberOfTownHouseBedroomMoreThanFive || 0,
          },
        ],
        showStudios: false,
        studiosCount: 0,
      })}
      {/* Penthouse */}
      {renderPropertyCard({
        icon: (
          <Building08
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("penthouse"),
        badgeLabel: `No. ${specifications?.numberOfPentHouse}`,
        minArea: specifications?.minPentHouseArea,
        maxArea: specifications?.maxPentHouseArea,
        unitTypesLeft: [
          {
            label: "x1",
            value: specifications?.numberOfPentHouseOneBedroom || 0,
          },
          {
            label: "x3",
            value: specifications?.numberOfPentHouseThreeBedroom || 0,
          },
          {
            label: "x5",
            value: specifications?.numberOfPentHouseFiveBedroom || 0,
          },
        ],
        unitTypesRight: [
          {
            label: "x2",
            value: specifications?.numberOfPentHouseTowBedroom || 0,
          },
          {
            label: "x4",
            value: specifications?.numberOfPentHouseFourBedroom || 0,
          },
          {
            label: "6+",
            value: specifications?.numberOfPentHouseBedroomMoreThanFive || 0,
          },
        ],
        showStudios: false,
        studiosCount: 0,
      })}
      {/* Retail Units */}
      {SimplePropertyCard({
        icon: (
          <Building02
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("retail-units"),
        badgeLabel: `No. ${specifications?.numberOfRetail}`,
        mainValue: `${specifications?.retailUnitGrossArea} sq.ft`,
        subValue: translate("gross-fq-footage"),
      })}
      {/* Office */}
      {SimplePropertyCard({
        icon: (
          <Briefcase01
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("office"),
        badgeLabel: `No. ${specifications?.numberOfOffices}`,
        mainValue: `${specifications?.officeGrossArea} sq.ft`,
        subValue: translate("gross-fq-footage"),
      })}
      {/* Labor Camp */}
      {SimplePropertyCard({
        icon: (
          <Building02
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("labor-camp"),
        badgeLabel: null,
        mainValue: `${specifications?.laborCampGrossArea} sq.ft`,
        subValue: translate("gross-fq-footage"),
      })}
      {/* Warehouse */}
      {SimplePropertyCard({
        icon: (
          <Warehouse
            width="24"
            height="24"
            fill={palette.foreground.secondary}
          />
        ),
        title: translate("warehouse"),
        badgeLabel: null,
        mainValue: `${specifications?.wareHouseGrossArea} sq.ft`,
        subValue: translate("gross-fq-footage"),
      })}
    </Box>
  );
}

export default Specifications;
